import { useState } from 'react';
import { PageHeader, Button, ConfirmDialog } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { usePayers } from '../hooks/usePayers';
import { useSetupReadiness } from '../hooks/useSetupReadiness';
import { PayersTable } from '../components/PayersTable';
import { AddPayerPanel } from '../components/AddPayerPanel';
import { SetupReadinessBanner } from '../components/SetupReadinessBanner';
import type { LocationPayerRow } from '../api/payersApi';

export function PayersPage() {
  const { payers, isLoading, removePayer } = usePayers();
  const { readiness } = useSetupReadiness();
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [removingPayer, setRemovingPayer] = useState<LocationPayerRow | null>(null);

  // Adding a payer requires the location to already be able to authenticate
  // on a call — otherwise there's nothing valid to charter or verify with
  // (backend enforces this too; this just avoids the round-trip failure).
  const canAddPayers =
    !!readiness &&
    readiness.hardGates.legalNamePresent &&
    readiness.hardGates.tinValid &&
    readiness.hardGates.groupNpiValid &&
    readiness.hardGates.hasProviderWithValidNpi;

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Payers"
        subtitle="The insurance companies this location works with, and how the AI agent reaches each one. Phone maps are maintained by the PracticeNova team."
        actions={
          <Button
            onClick={() => setShowAddPanel((v) => !v)}
            disabled={!canAddPayers}
            title={canAddPayers ? undefined : 'Complete Practice Credentials before adding payers'}
          >
            + Add payer
          </Button>
        }
      />

      <SetupReadinessBanner />

      {showAddPanel && canAddPayers && <AddPayerPanel onClose={() => setShowAddPanel(false)} />}

      <PayersTable payers={payers} isLoading={isLoading} onRemove={setRemovingPayer} />

      <p className={patterns.timestamp}>
        STRATEGY — VOICE: the AI agent calls this payer. STAFF: verifications route to your review
        assignee until the phone map is charted and approved. Provider-line overrides always take
        precedence over the directory number.
      </p>

      <ConfirmDialog
        isOpen={!!removingPayer}
        onClose={() => setRemovingPayer(null)}
        onConfirm={() => {
          if (removingPayer) {
            removePayer.mutate(removingPayer.id, { onSuccess: () => setRemovingPayer(null) });
          }
        }}
        title="Remove this payer?"
        message={
          removingPayer
            ? `This removes "${removingPayer.payerName}" from this location's payer list. It won't affect the agency directory or other locations.`
            : ''
        }
        confirmLabel="Remove"
        variant="destructive"
        isLoading={removePayer.isPending}
      />
    </div>
  );
}
